import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { logout } from './authSlice';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002';

const defaultMeta = {
  page: 1,
  page_size: 10,
  total_items: 0,
  total_pages: 0
};

const formatDateForQuery = (date) => date.toISOString().split('T')[0];

const getDefaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  return {
    start: formatDateForQuery(start),
    end: formatDateForQuery(end)
  };
};

const defaultDateRange = getDefaultDateRange();

const defaultQuery = {
  page: 1,
  pageSize: 10,
  startDate: defaultDateRange.start,
  endDate: defaultDateRange.end,
  search: '',
  sortBy: 'created_datetime',
  sortDirection: 'desc'
};

const initialState = {
  items: [],
  meta: defaultMeta,
  loading: false,
  error: null,
  query: defaultQuery,
  exportStatus: 'idle',
  exportError: null
};

const buildAuthHeaders = (getState, extra = {}) => {
  const { auth } = getState();
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...extra
  };

  if (auth?.accessToken) {
    headers.Authorization = `Bearer ${auth.accessToken}`;
  }

  return headers;
};

const handleUnauthorized = (dispatch) => {
  dispatch(logout());
  if (typeof window !== 'undefined') {
    window.location.replace('/login');
  }
};

const handleApiResponse = async (response, dispatch) => {
  if (response.status === 401) {
    handleUnauthorized(dispatch);
    throw new Error('Unauthorized');
  }

  const contentType = response.headers.get('content-type');
  let data;

  if (contentType && contentType.includes('application/json')) {
    data = await response.json();
  } else {
    data = await response.text();
  }

  if (!response.ok) {
    const message =
      (data &&
        typeof data === 'object' &&
        (data.resultDesc || data.resultMessage || data.message)) ||
      response.statusText ||
      'Request failed';
    throw new Error(message);
  }

  if (
    data &&
    typeof data === 'object' &&
    Object.prototype.hasOwnProperty.call(data, 'resultCode') &&
    Number(data.resultCode) !== 200
  ) {
    if (Number(data.resultCode) === 401) {
      handleUnauthorized(dispatch);
    }
    const message = data.resultDesc || data.resultMessage || data.message || 'Request failed';
    throw new Error(message);
  }

  if (data && typeof data === 'object' && Number(data.resultCode) === 401) {
    handleUnauthorized(dispatch);
    throw new Error(data.resultDesc || data.resultMessage || data.message || 'Unauthorized');
  }

  return data;
};

const buildQueryString = (params = {}) => {
  const merged = {
    ...defaultQuery,
    ...params
  };

  const searchParams = new URLSearchParams();

  if (merged.page) searchParams.set('page', merged.page);
  if (merged.pageSize) searchParams.set('page_size', merged.pageSize);
  if (merged.startDate) searchParams.set('start_date', merged.startDate);
  if (merged.endDate) searchParams.set('end_date', merged.endDate);
  if (merged.search) searchParams.set('search', merged.search);
  if (merged.sortBy) searchParams.set('sort_by', merged.sortBy);
  if (merged.sortDirection) searchParams.set('sort_direction', merged.sortDirection);

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};

const transformItem = (item) => ({
  id: item.id,
  created_datetime: item.created_datetime || item.created_at || item.createdAt,
  admin: {
    id: item.admin?.id ?? item.admin_id ?? null,
    name: item.admin?.name || item.admin_name || 'Unknown',
    email: item.admin?.email || item.admin_email || ''
  },
  admin_name: item.admin?.name || item.admin_name || 'Unknown',
  admin_email: item.admin?.email || item.admin_email || '',
  activity_desc: item.activity_desc || '',
  activity_type: item.activity_type || null,
  activity_result: item.activity_result || null
});

export const fetchAuditTrail = createAsyncThunk(
  'auditTrail/fetchAuditTrail',
  async (params, { getState, dispatch, rejectWithValue }) => {
    try {
      const queryString = buildQueryString(params);
      const response = await fetch(`${API_BASE_URL}/api/audit-trail${queryString}`, {
        method: 'GET',
        headers: buildAuthHeaders(getState)
      });

      const data = await handleApiResponse(response, dispatch);
      const rawItems = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.resultData?.data)
        ? data.resultData.data
        : Array.isArray(data?.resultData)
        ? data.resultData
        : [];
      const items = rawItems.map(transformItem);
      const rawMeta = data?.meta || data?.resultData?.meta;
      const meta = rawMeta
        ? {
            page: Number(rawMeta.page ?? defaultMeta.page),
            page_size: Number(rawMeta.page_size ?? defaultMeta.page_size),
            total_items: Number(rawMeta.total_items ?? defaultMeta.total_items),
            total_pages: Number(rawMeta.total_pages ?? defaultMeta.total_pages)
          }
        : defaultMeta;

      return {
        items,
        meta,
        query: {
          page: params?.page ?? defaultQuery.page,
          pageSize: params?.pageSize ?? defaultQuery.pageSize,
          startDate: params?.startDate ?? defaultQuery.startDate,
          endDate: params?.endDate ?? defaultQuery.endDate,
          search: params?.search ?? defaultQuery.search,
          sortBy: params?.sortBy ?? defaultQuery.sortBy,
          sortDirection: params?.sortDirection ?? defaultQuery.sortDirection
        }
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch audit trail.');
    }
  }
);

export const exportAuditTrail = createAsyncThunk(
  'auditTrail/exportAuditTrail',
  async (params, { getState, dispatch, rejectWithValue }) => {
    try {
      const searchParams = new URLSearchParams();
      const merged = { ...defaultQuery, ...params };
      if (merged.startDate) searchParams.set('start_date', merged.startDate);
      if (merged.endDate) searchParams.set('end_date', merged.endDate);
      if (merged.search) searchParams.set('search', merged.search);
      if (merged.sortBy) searchParams.set('sort_by', merged.sortBy);
      if (merged.sortDirection) searchParams.set('sort_direction', merged.sortDirection);
      searchParams.set('all', 'true');

      const queryString = searchParams.toString();
      const exportUrl = `${API_BASE_URL}/api/audit-trail/export` + (queryString ? `?${queryString}` : '');

      const response = await fetch(exportUrl, {
        method: 'GET',
        headers: buildAuthHeaders(getState, {
          Accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/json'
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleUnauthorized(dispatch);
        }
        const message = await response.text();
        throw new Error(message || response.statusText || 'Failed to export audit trail.');
      }

      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const payload = await response.json();
        if (Number(payload?.resultCode) === 401) {
          handleUnauthorized(dispatch);
        }
        if (Number(payload?.resultCode) !== 200) {
          throw new Error(
            payload?.resultDesc || payload?.resultMessage || payload?.message || 'Failed to export audit trail.'
          );
        }
        // When backend responds with JSON success, nothing to download.
        throw new Error(payload?.resultDesc || 'No file available for download.');
      }

      const blob = await response.blob();
      if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        const disposition = response.headers.get('content-disposition') || '';
        const match = disposition.match(/filename="?(.+?)"?$/i);
        const filename = match ? match[1] : `audit-trail-${new Date().toISOString().slice(0, 10)}.xlsx`;

        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(url);
      }

      return true;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to export audit trail.');
    }
  }
);

const auditTrailSlice = createSlice({
  name: 'auditTrail',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAuditTrail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAuditTrail.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.meta = action.payload.meta;
        state.query = action.payload.query;
      })
      .addCase(fetchAuditTrail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch audit trail.';
      })
      .addCase(exportAuditTrail.pending, (state) => {
        state.exportStatus = 'loading';
        state.exportError = null;
      })
      .addCase(exportAuditTrail.fulfilled, (state) => {
        state.exportStatus = 'succeeded';
      })
      .addCase(exportAuditTrail.rejected, (state, action) => {
        state.exportStatus = 'failed';
        state.exportError = action.payload || 'Failed to export audit trail.';
      });
  }
});

export default auditTrailSlice.reducer;
